import React, { useState, useEffect } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import './App.css';
import axios from 'axios';
import IntakeLandingPage from './components/IntakeLandingPage';
import ProgressTracker from './components/ProgressTracker';
import BusinessStep from './components/BusinessStep';
import PrincipalStep from './components/PrincipalStep';
import ProcessingStep from './components/ProcessingStep';
import DocumentsStep from './components/DocumentsStep';
import UploadDocuments from './components/UploadDocuments';
import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import ApplicationQueue from './components/admin/ApplicationQueue';
import ApplicationDetail from './components/admin/ApplicationDetail';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MerchantApp() {
  const [showFullForm, setShowFullForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [draftToken, setDraftToken] = useState(null);
  const [paysecAppId, setPaysecAppId] = useState(null);
  const [formData, setFormData] = useState({
    processingMethod: 'Acquiring',
    company: { address: {} },
    dba: { sameAsCompany: 'No', address: {} },
    businessLocation: {},
    corporateContact: {},
    customerServiceContact: {},
    principals: [],
    processing: { volumes: {}, sales: {} },
    documents: []
  });
  const [loading, setLoading] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeToken, setResumeToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const continueApp = params.get('continue');

    if (token) {
      setShowFullForm(true);
      loadDraft(token);
    } else if (continueApp === 'true') {
      setShowFullForm(true);
      createNewDraft();
    }
  }, []);

  useEffect(() => {
    if (draftToken && currentStep > 1) {
      const autoSaveTimer = setTimeout(() => {
        saveDraft();
      }, 30000);
      return () => clearTimeout(autoSaveTimer);
    }
  }, [formData, draftToken]);

  const createNewDraft = async () => {
    try {
      const response = await axios.post(`${API}/application/create`, {
        processingMethod: 'Acquiring'
      });
      setDraftToken(response.data.draftToken);
      toast.success('New application started');
    } catch (error) {
      console.error('Error creating draft:', error);
      toast.error('Failed to create application');
    }
  };

  const loadDraft = async (token) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/application/${token}`);
      setFormData(response.data);
      setDraftToken(token);
      const status = response.data.status;
      if (['in_review', 'submitted', 'approved', 'rejected'].includes(status)) {
        if (response.data.paysecApplicationId) {
          setPaysecAppId(response.data.paysecApplicationId);
        }
        setSubmitted(true);
        return;
      }
      toast.success('Application loaded successfully');
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error('Failed to load application');
      createNewDraft();
    } finally {
      setLoading(false);
    }
  };

  const saveDraft = async () => {
    if (!draftToken) return;
    try {
      await axios.put(`${API}/application/${draftToken}`, formData);
      toast.success('Progress saved');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save progress');
    }
  };

  const handleNext = async () => {
    await saveDraft();
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await saveDraft();
      const response = await axios.post(`${API}/application/${draftToken}/submit`);
      if (response.data.status === 'success') {
        if (response.data.paysecApplicationId) {
          setPaysecAppId(response.data.paysecApplicationId);
        }
        toast.success('Application submitted successfully!');
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit application. Please try again.';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const copyDraftLink = () => {
    const link = `${window.location.origin}?token=${draftToken}`;
    navigator.clipboard.writeText(link);
    toast.success('Draft link copied to clipboard!');
  };

  const handleResume = () => {
    if (resumeToken) {
      window.location.href = `?token=${resumeToken}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-slate-200 text-xl">Loading...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full">
          <div className="bg-slate-800 p-12 rounded-2xl shadow-2xl text-center">
            <svg className="w-20 h-20 text-green-400 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-slate-100 mb-4">Application Submitted!</h2>
            <p className="text-slate-300 text-lg mb-6">
              Your merchant application has been successfully submitted for review.
            </p>

            <div className="bg-slate-900 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Application Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Application ID</span>
                  <span className="text-slate-100 font-mono text-sm font-bold">{paysecAppId || draftToken}</span>
                </div>
                {paysecAppId && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Reference</span>
                    <span className="text-slate-500 font-mono text-xs">{draftToken}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-400">Status</span>
                  <span className="text-yellow-400 font-medium">Under Review</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Company</span>
                  <span className="text-slate-100">{formData.company?.name || '—'}</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">What Happens Next?</h3>
              <ol className="space-y-3 text-slate-300">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-sm font-semibold text-slate-200">1</span>
                  <span>Our team will review your application and supporting documents.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-sm font-semibold text-slate-200">2</span>
                  <span>You may be contacted if additional information is needed.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-sm font-semibold text-slate-200">3</span>
                  <span>Once approved, you'll receive your merchant account credentials via email.</span>
                </li>
              </ol>
            </div>

            <a
              href={`/upload-documents/${draftToken}`}
              className="inline-block bg-slate-700 text-slate-100 hover:bg-slate-600 font-semibold py-3 px-8 rounded-lg transition-colors mb-6"
            >
              Upload Additional Documents
            </a>

            <p className="text-slate-500 text-sm">
              Save your Application ID ({paysecAppId || draftToken}) for future reference. You can close this page.
            </p>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-8 border-t border-slate-700/50">
            <div className="flex flex-col items-center gap-4">
              <img src="/paysec-logo.png" alt="PaySec" className="h-8 opacity-80" />
              <p className="text-slate-400 text-sm italic">
                Copyright &copy; {new Date().getFullYear()} &mdash; PaySec LLC. All Rights Reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!showFullForm) {
    return <IntakeLandingPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-100 mb-3">Merchant Onboarding</h1>
          <p className="text-slate-400 text-lg">PaySec Merchant Application</p>
          {draftToken && (
            <div className="mt-4 flex justify-center gap-3">
              <Button
                onClick={copyDraftLink}
                variant="outline"
                className="text-sm bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                data-testid="copy-draft-link-btn"
              >
                Copy Draft Link
              </Button>
              <Button
                onClick={() => setShowResumeModal(true)}
                variant="outline"
                className="text-sm bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                Resume Application
              </Button>
            </div>
          )}
        </header>

        <ProgressTracker currentStep={currentStep} />

        <div className="bg-slate-850 rounded-xl p-8 shadow-2xl mb-8" data-testid={`step-${currentStep}`}>
          {currentStep === 1 && <BusinessStep formData={formData} setFormData={setFormData} />}
          {currentStep === 2 && <PrincipalStep formData={formData} setFormData={setFormData} />}
          {currentStep === 3 && <ProcessingStep formData={formData} setFormData={setFormData} />}
          {currentStep === 4 && <DocumentsStep formData={formData} draftToken={draftToken} />}
        </div>

        <div className="flex justify-between items-center">
          <Button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="previous-btn"
          >
            Previous
          </Button>

          <p className="text-slate-400 text-sm">
            All fields marked with * are required
          </p>

          {currentStep < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-slate-200 text-slate-900 hover:bg-slate-100 font-semibold"
              data-testid="next-btn"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-600 text-white hover:bg-green-500 font-semibold"
              data-testid="submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex justify-center items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="18" fill="#0e7490" />
              <text x="20" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">AICPA</text>
              <text x="20" y="22" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">SOC</text>
              <text x="20" y="30" textAnchor="middle" fill="#a5f3fc" fontSize="5">Certified</text>
            </svg>
            <span className="text-slate-400 text-xs font-medium">AICPA SOC<br/>Certified</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <rect x="2" y="2" width="36" height="36" rx="4" fill="#1e293b" stroke="#4ade80" strokeWidth="2" />
              <text x="20" y="13" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">PCI</text>
              <text x="20" y="21" textAnchor="middle" fill="#94a3b8" fontSize="4.5">DSS</text>
              <path d="M12 27 L18 33 L30 21" stroke="#4ade80" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-slate-400 text-xs font-medium">PCI DSS<br/>Service Level 1</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <path d="M20 2 L36 10 L36 22 C36 30 28 36 20 38 C12 36 4 30 4 22 L4 10 Z" fill="#334155" stroke="#60a5fa" strokeWidth="1.5" />
              <text x="20" y="18" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">SOC 2</text>
              <text x="20" y="27" textAnchor="middle" fill="#93c5fd" fontSize="5.5">Type II</text>
            </svg>
            <span className="text-slate-400 text-xs font-medium">SOC 2<br/>Type II</span>
          </div>

          <div className="flex items-center gap-2 bg-slate-800/60 px-4 py-3 rounded-lg border border-slate-700/50">
            <svg className="w-10 h-10" viewBox="0 0 40 40" fill="none">
              <rect x="2" y="2" width="36" height="36" rx="6" fill="#166534" />
              <rect x="13" y="8" width="14" height="10" rx="3" stroke="white" strokeWidth="2" fill="none" />
              <rect x="10" y="16" width="20" height="16" rx="2" fill="white" />
              <circle cx="20" cy="24" r="3" fill="#166534" />
              <rect x="19" y="26" width="2" height="4" rx="1" fill="#166534" />
            </svg>
            <span className="text-slate-400 text-xs font-medium">256-Bit SSL<br/>Encrypted</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-8 border-t border-slate-700/50">
          <div className="flex flex-col items-center gap-4">
            <img src="/paysec-logo.png" alt="PaySec" className="h-8 opacity-80" />
            <p className="text-slate-400 text-sm italic">
              Copyright &copy; {new Date().getFullYear()} &mdash; PaySec LLC. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>

      {showResumeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 p-8 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-semibold text-slate-100 mb-4">Resume Application</h3>
            <Label className="text-slate-200 mb-2 block">Enter your draft token:</Label>
            <Input
              value={resumeToken}
              onChange={(e) => setResumeToken(e.target.value)}
              placeholder="Enter token"
              className="mb-4 bg-slate-900 border-slate-700 text-slate-100"
            />
            <div className="flex gap-3">
              <Button onClick={handleResume} className="flex-1 bg-slate-200 text-slate-900 hover:bg-slate-100">
                Resume
              </Button>
              <Button onClick={() => setShowResumeModal(false)} variant="outline" className="flex-1 border-slate-600 text-slate-200 hover:bg-slate-700">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VerifyEmail() {
  const { token } = useParams();
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    axios.post(`${API}/verify-email/${token}`)
      .then(res => {
        setStatus(res.data.status === 'already_verified' ? 'already_verified' : 'verified');
      })
      .catch(err => {
        setStatus('error');
        setError(err.response?.data?.detail || 'Verification failed');
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-slate-800 p-12 rounded-2xl shadow-2xl text-center">
        {status === 'verifying' && (
          <>
            <div className="mb-6 animate-spin w-16 h-16 border-4 border-slate-600 border-t-green-400 rounded-full mx-auto" />
            <h2 className="text-2xl font-bold text-slate-100">Verifying your email...</h2>
          </>
        )}
        {status === 'verified' && (
          <>
            <svg className="w-20 h-20 text-green-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-slate-100 mb-4">Email Verified!</h2>
            <p className="text-slate-300 text-lg mb-6">
              Your email has been confirmed. We're sending you a secure onboarding link now — check your inbox shortly.
            </p>
          </>
        )}
        {status === 'already_verified' && (
          <>
            <svg className="w-20 h-20 text-blue-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-slate-100 mb-4">Already Verified</h2>
            <p className="text-slate-300 text-lg mb-6">
              Your email was already confirmed. Check your inbox for the onboarding link we sent earlier.
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <svg className="w-20 h-20 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-3xl font-bold text-slate-100 mb-4">Verification Failed</h2>
            <p className="text-slate-300 text-lg mb-6">{error}</p>
          </>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/upload-documents/:token" element={<UploadDocuments />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route path="applications" element={<ApplicationQueue />} />
          <Route path="applications/:token" element={<ApplicationDetail />} />
        </Route>
        <Route path="*" element={<MerchantApp />} />
      </Routes>
    </>
  );
}

export default App;
